import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Stack, Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import IconButton from './IconButton';
import { Icon } from './Icons';
import { withRoomClientContext } from '../lib/RoomClientContext';
import { toggleMicrophone } from '../store/slices/deviceSlice';
import { debounce } from '@mui/material';

function MicIndicator({ stream }) {
  const debouncedToggleMicrophone = useRef(
    debounce(() => dispatch(toggleMicrophone()), 200)
  ).current;
  const [volumeLevel, setVolumeLevel] = useState(0); // Range 0 to 1
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const { muted } = useSelector((state) => state.device);

  useEffect(() => {
    if (!stream) return;

    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(stream);
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    analyser.fftSize = 32;
    microphone.connect(analyser);

    const updateVolume = () => {
      analyser.getByteFrequencyData(dataArray);
      const volume =
        dataArray.reduce((a, b) => a + b, 0) / dataArray.length / 32;
      const normalizedVolume = Math.min(volume, 1);
      setVolumeLevel(normalizedVolume); // Normalize volume level between 0 and 1

      requestAnimationFrame(updateVolume);
    };

    updateVolume();

    return () => {
      audioContext.close();
    };
  }, [stream, dispatch]);

  const scaledVolumeLevel = volumeLevel * 20; // Scale volume level for better visual representation

  return (
    <Stack alignItems="center" width="100px">
      <IconButton
        onClick={debouncedToggleMicrophone}
        aria-label={muted ? t('Turn microphone on') : t('Turn microphone off')}
        aria-pressed={!muted}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            overflow: 'hidden',
            width: 'calc(100% + 12px)', // Subtract padding from IconButton
            height: 'calc(100% + 12px)', // Subtract padding from IconButton
            margin: '-6px', // Negative to offset padding from IconButton
            borderRadius: '100%',
            padding: '6px', // replicate padding from IconButton
          }}
        >
          {/* Background fill based on volume level */}
          <Box
            sx={(theme) => ({
              position: 'absolute',
              left: 0,
              bottom: -3,
              width: '100%',
              height: `${scaledVolumeLevel * 100}%`,
              backgroundColor: theme.palette.primary.contrast,
              transition: 'height 0.1s ease',
              display: muted ? 'none' : 'block',
            })}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Icon
              name={muted ? 'micOff' : 'micOn'}
              color={theme.palette.secondary.main}
            />
          </Box>
        </Box>
      </IconButton>
      <Typography
        sx={{
          textAlign: 'center',
          fontSize: '12px', // Adjust font size as needed
        }}
      >
        {muted ? t('Turn on') : t('Turn off')}
      </Typography>
    </Stack>
  );
}

const MicIndicatorWrapper = ({ roomClient }) => {
  const stream = roomClient.streamHandler._audioStream;
  return <MicIndicator stream={stream} />;
};

export default withRoomClientContext(MicIndicatorWrapper);
